import type { CurrentUserResponse } from "./api-types";
import {
  getEmptyOnboardingData,
  type AgentSharingLevel,
  type OnboardingResponse,
  type OnboardingData,
  type OnboardingSession,
} from "./onboarding-types";
import { getTargetLocationError } from "./target-location";
import {
  targetUnresolvableMessage,
  targetUnresolvableReason,
} from "./target-unresolvable";

export const ONBOARDING_SESSION_KEY = "sill-onboarding-session";
export const ONBOARDING_RESULT_KEY = "sill-onboarding-result";
export const MARKETPLACE_CLAIM_QUERY_KEY = "claim";
export const MARKETPLACE_PUBLISH_INTENT_QUERY_KEY = "marketplace_intent";
export const MARKETPLACE_PUBLISH_INTENT_VALUE = "publish";
export const REFERRAL_ATTRIBUTION_QUERY_KEY = "referral_token";
/**
 * The Ask Sill question seed the marketing landing deep-links in
 * (`AskSillDemo` → `/onboarding?q=…`). Read it through
 * `getQuestionSeedFromParams` — both the chat door that answers it and the
 * posture resolver that routes to that door must agree on what counts as a
 * question, or a visitor gets routed somewhere their seed is dropped.
 */
export const QUESTION_SEED_QUERY_KEY = "q";
export const ONBOARDING_STEP_COUNT = 11;
export const ONBOARDING_STEP_QUERY_MAP = {
  budget: 7,
  credit_score: 4,
  email_capture: 10,
  housing_situation: 9,
  income: 2,
  monthly_debts: 5,
  savings: 3,
  target_area: 1,
  timeline: 8,
  work_history: 6,
} as const;

export const ONBOARDING_CURRENCY_LIMITS = {
  budget: { max: 25_000_000, min: 50_000 },
  householdIncome: { max: 10_000_000, min: 1 },
  monthlyBudget: { max: 1_000_000, min: 1 },
  monthlyDebts: { max: 1_000_000, min: 0 },
  savings: { max: 10_000_000, min: 0 },
} as const;

type LocalDevOnboardingEnv = {
  DEV?: boolean;
  VITE_ENABLE_LOCAL_ONBOARDING_SKIP?: string;
};

interface ValidationResult {
  error: string | null;
  isValid: boolean;
}

interface CurrencyValidationOptions {
  label: string;
  max: number;
  min: number;
  required?: boolean;
}

export function resolveOnboardingQueryStep(searchParams: URLSearchParams) {
  const rawStep = searchParams.get("step");

  if (!rawStep) {
    return null;
  }

  const mappedStep =
    ONBOARDING_STEP_QUERY_MAP[
      rawStep as keyof typeof ONBOARDING_STEP_QUERY_MAP
    ];

  if (typeof mappedStep === "number") {
    return mappedStep;
  }

  const numericStep = Number.parseInt(rawStep, 10);

  if (
    Number.isFinite(numericStep) &&
    numericStep >= 0 &&
    numericStep < ONBOARDING_STEP_COUNT
  ) {
    return numericStep;
  }

  return null;
}

export function getAgentReferralFromParams(searchParams: URLSearchParams) {
  const agentInviteCode =
    normalizeOptionalText(searchParams.get("agent_invite_code")) ??
    normalizeOptionalText(searchParams.get("ref")) ??
    normalizeOptionalText(searchParams.get("invite_code"));
  const agentId = normalizeOptionalText(searchParams.get("agent_id"));
  const agentName = normalizeOptionalText(searchParams.get("agent_name"));
  const agentAvatar = normalizeOptionalText(searchParams.get("agent_avatar"));

  if (!agentInviteCode && (!agentId || !agentName)) {
    return null;
  }

  return {
    agentInviteCode,
    agentAvatar,
    agentId,
    agentName,
  };
}

/**
 * Reads the opaque marketplace claim handoff separately from agent referral
 * parameters. It is session-only pre-account state for the post-signup claim
 * reader; it is never sent as profile input or analytics metadata.
 */
export function getMarketplaceClaimFromParams(searchParams: URLSearchParams): string | null {
  return normalizeMarketplaceClaimToken(searchParams.get(MARKETPLACE_CLAIM_QUERY_KEY));
}

/**
 * Reads the Ask Sill question seed. Returns `null` for an absent, empty, or
 * whitespace-only value — an empty submit on the landing page navigates here
 * with no `q` at all, and a stray `?q=` is not a question anyone promised to
 * answer.
 */
export function getQuestionSeedFromParams(
  searchParams: URLSearchParams,
): string | null {
  return normalizeOptionalText(searchParams.get(QUESTION_SEED_QUERY_KEY));
}

/** Only the public-share entrypoint may request this fixed completion target. */
export function getMarketplacePublishIntentFromParams(searchParams: URLSearchParams): boolean {
  return searchParams.get(MARKETPLACE_PUBLISH_INTENT_QUERY_KEY) === MARKETPLACE_PUBLISH_INTENT_VALUE;
}

/**
 * Reads the public-token handoff used by buyer-brag short links and
 * marketplace claims. This is intentionally separate from the legacy `ref`
 * agent-invite parameter: opaque referral links must never be interpreted as
 * agent invite codes or sent as profile input.
 */
export function getReferralAttributionTokenFromParams(searchParams: URLSearchParams): string | null {
  return (
    normalizeMarketplaceClaimToken(searchParams.get(REFERRAL_ATTRIBUTION_QUERY_KEY)) ??
    getMarketplaceClaimFromParams(searchParams)
  );
}

/**
 * Persists public-token handoffs before an auth or onboarding transition can
 * replace the URL. The claim remains available to its existing post-signup
 * claim reader; attribution consumes its own session field later.
 */
export function captureReferralAttributionHandoff(searchParams: URLSearchParams): void {
  const referralAttributionToken = getReferralAttributionTokenFromParams(searchParams);
  const marketplaceClaimToken = getMarketplaceClaimFromParams(searchParams);
  const marketplacePublishIntent = getMarketplacePublishIntentFromParams(searchParams);
  if (!referralAttributionToken && !marketplaceClaimToken && !marketplacePublishIntent) return;

  const session = loadOnboardingSession();
  saveOnboardingSession({
    data: session?.data ?? getEmptyOnboardingData(),
    isComplete: session?.isComplete ?? false,
    marketplaceClaimToken: marketplaceClaimToken ?? session?.marketplaceClaimToken ?? null,
    marketplacePublishIntent: marketplacePublishIntent || session?.marketplacePublishIntent === true,
    referralAttributionToken: referralAttributionToken ?? session?.referralAttributionToken ?? null,
    step: session?.step ?? 0,
  });
}

/**
 * Builds the authenticated claim-resume route from an opaque, session-only
 * handoff. Keep this in the onboarding boundary so no other surface starts
 * treating the claim as profile or analytics data.
 */
export function getMarketplaceClaimResumePath(value: unknown): string | null {
  const token = normalizeMarketplaceClaimToken(value);
  return token
    ? `/marketplace/claims/${encodeURIComponent(token)}`
    : null;
}

export function getMarketplacePublishResumePath(value: unknown): string | null {
  return value === true ? "/dashboard?view=passport#passport-publish-action" : null;
}

function normalizeMarketplaceClaimToken(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const token = value.trim();
  return /^[A-Za-z0-9_-]{1,512}$/.test(token) ? token : null;
}

/**
 * Query-string keys that carry agent referral / attribution across the
 * onboarding surfaces. `getAgentReferralFromParams` reads them and
 * `OnboardingFlow`'s "not my agent" control strips them. Kept in one place so
 * any redirect between onboarding doors (chat → quick form) forwards the full
 * set instead of silently dropping the buyer's agent code.
 */
export const REFERRAL_PARAM_KEYS = [
  "agent_invite_code",
  "agent_id",
  "agent_name",
  "agent_avatar",
  "invite_code",
  "ref",
] as const;

/** Public opaque-token handoffs that must survive a chat → form redirect. */
export const REFERRAL_ATTRIBUTION_PARAM_KEYS = [
  REFERRAL_ATTRIBUTION_QUERY_KEY,
  MARKETPLACE_CLAIM_QUERY_KEY,
] as const;

/**
 * Copy any present referral / attribution params from `from` into `to`,
 * preserving the buyer's agent code when navigating between onboarding doors
 * (e.g. the chat door's "Switch to quick form" escape). The code lives only in
 * the URL — the form door re-reads it via `getAgentReferralFromParams` — so a
 * redirect that builds fresh params must forward these keys or attribution is
 * lost. Only present, non-empty keys are copied; existing values in `to` for
 * other keys are never cleared. Returns `to` for chaining.
 */
export function forwardReferralParams(
  from: URLSearchParams,
  to: URLSearchParams,
): URLSearchParams {
  for (const key of [...REFERRAL_PARAM_KEYS, ...REFERRAL_ATTRIBUTION_PARAM_KEYS]) {
    const value = from.get(key);
    if (value !== null && value !== "") {
      to.set(key, value);
    }
  }
  return to;
}

export function getCaptureStepCta(data: OnboardingData) {
  return data.agentName
    ? `See my full results and share with ${data.agentName}`
    : "See my full results";
}

export function getCompletionPrimaryCta(data: OnboardingData) {
  return data.agentName
    ? `Continue with ${data.agentName}`
    : "Start saving with Sill";
}

export function getCompletionSecondaryCta(data: OnboardingData) {
  return data.agentName ? "Choose a different agent" : "Connect with an Agent";
}

export function isValidEmail(email: string | null) {
  if (!email) {
    return false;
  }

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function normalizeOptionalText(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function isLocalDevOnboardingSkipEnabled(
  env: LocalDevOnboardingEnv = import.meta.env,
) {
  if (!env.DEV) {
    return false;
  }

  const rawValue = normalizeOptionalText(
    env.VITE_ENABLE_LOCAL_ONBOARDING_SKIP,
  )?.toLowerCase();

  return !(
    rawValue === "0" ||
    rawValue === "false" ||
    rawValue === "no" ||
    rawValue === "off"
  );
}

export function getLocalDevOnboardingPreset(
  options: { now?: Date; random?: () => number } = {},
): OnboardingData {
  const now = options.now ?? new Date();
  const random = options.random ?? Math.random;
  const leaseExpiration = new Date(now);
  leaseExpiration.setFullYear(leaseExpiration.getFullYear() + 1);
  const emailNumber = Math.min(1_000, Math.floor(random() * 1_000) + 1);

  return {
    ...getEmptyOnboardingData(),
    budget: 575_000,
    buyerType: "first-time",
    creditScore: "excellent",
    email: `${emailNumber}+clerk_test@usesill.com`,
    employmentMonths: 36,
    firstName: "Local",
    householdIncome: 180_000,
    housingSituation: "renting",
    leaseExpiration: formatDateInputValue(leaseExpiration),
    monthlyBudget: 4_200,
    monthlyDebts: 650,
    savings: 90_000,
    targetArea: "Austin, TX",
    timeline: "3-6months",
  };
}

export function sanitizeOnboardingData(data: OnboardingData): OnboardingData {
  return {
    ...data,
    agentAvatar: normalizeOptionalText(data.agentAvatar),
    agentId: normalizeOptionalText(data.agentId),
    agentInviteCode:
      normalizeOptionalText(data.agentInviteCode)?.toUpperCase() ?? null,
    manualInviteCode:
      normalizeOptionalText(data.manualInviteCode)?.toUpperCase() ?? null,
    agentName: normalizeOptionalText(data.agentName),
    email: normalizeOptionalText(data.email),
    firstName: normalizeOptionalText(data.firstName),
    leaseExpiration: normalizeOptionalText(data.leaseExpiration),
    targetArea: normalizeOptionalText(data.targetArea),
  };
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong",
) {
  if (!(error instanceof Error)) {
    return fallback;
  }

  // A `target_unresolvable` 422 has no `error` field - its body is
  // {code, reason} - so without this the raw JSON would be shown. No label
  // here: onboarding and the property form report the area as "that area".
  const unresolvable = targetUnresolvableReason(error);
  if (unresolvable) {
    return targetUnresolvableMessage(unresolvable);
  }

  if (!error.message) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(error.message) as { error?: string };
    return parsed.error ?? error.message;
  } catch {
    return error.message;
  }
}

export function getDefaultAgentSharingLevel(): AgentSharingLevel {
  // SEC-14: a new consumer→pro connection starts at status only. The buyer
  // can widen it from the sharing controls or later on the /team page; the
  // onboarding funnel asks explicitly (AgentSharingChoice).
  return "status_only";
}

export function getTargetAreaError(targetArea: string | null) {
  return getTargetLocationError(targetArea);
}

export function getEmailError(email: string | null) {
  const normalizedEmail = normalizeOptionalText(email);

  if (!normalizedEmail) {
    return "Email is required";
  }

  if (!isValidEmail(normalizedEmail)) {
    return "Enter a valid email";
  }

  return null;
}

export function getHouseholdIncomeError(householdIncome: number | null) {
  return validateCurrencyValue(householdIncome, {
    ...ONBOARDING_CURRENCY_LIMITS.householdIncome,
    label: "Household income",
  });
}

export function getSavingsError(savings: number | null) {
  return validateCurrencyValue(savings, {
    ...ONBOARDING_CURRENCY_LIMITS.savings,
    label: "Savings",
  });
}

export function getEmploymentMonthsError(employmentMonths: number | null) {
  if (employmentMonths === null) {
    return "Work history is required";
  }

  if (!Number.isFinite(employmentMonths) || employmentMonths < 0) {
    return "Choose a valid work-history option";
  }

  return null;
}

export function getMonthlyDebtsError(monthlyDebts: number | null) {
  return validateCurrencyValue(monthlyDebts, {
    ...ONBOARDING_CURRENCY_LIMITS.monthlyDebts,
    label: "Monthly debt payments",
  });
}

export function getTargetBudgetError(budget: number | null) {
  return validateCurrencyValue(budget, {
    ...ONBOARDING_CURRENCY_LIMITS.budget,
    label: "Target budget",
  });
}

export function getMonthlyBudgetError(monthlyBudget: number | null) {
  return validateCurrencyValue(monthlyBudget, {
    ...ONBOARDING_CURRENCY_LIMITS.monthlyBudget,
    label: "Monthly budget",
    required: false,
  });
}

export function getLeaseExpirationError(leaseExpiration: string | null) {
  const normalizedLeaseExpiration = normalizeOptionalText(leaseExpiration);

  if (!normalizedLeaseExpiration) {
    return null;
  }

  const leaseEnd = parseDateInput(normalizedLeaseExpiration);
  if (!leaseEnd) {
    return "Enter a valid lease end date";
  }

  const today = startOfLocalDay(new Date());
  if (leaseEnd < today) {
    return "Lease end date can't be in the past";
  }

  return null;
}

export function getOnboardingStepValidation(
  step: number,
  data: OnboardingData,
): ValidationResult {
  switch (step) {
    case 0:
      return buildValidationResult(
        data.buyerType ? null : "Buyer type is required",
      );
    case 1:
      return buildValidationResult(getTargetAreaError(data.targetArea));
    case 2:
      return buildValidationResult(
        getHouseholdIncomeError(data.householdIncome),
      );
    case 3:
      return buildValidationResult(getSavingsError(data.savings));
    case 4:
      return buildValidationResult(
        data.creditScore ? null : "Credit score is required",
      );
    case 5:
      return buildValidationResult(getMonthlyDebtsError(data.monthlyDebts));
    case 6:
      return buildValidationResult(
        getEmploymentMonthsError(data.employmentMonths),
      );
    case 7:
      return buildValidationResult(
        getTargetBudgetError(data.budget) ??
          getMonthlyBudgetError(data.monthlyBudget),
      );
    case 8:
      return buildValidationResult(
        data.timeline ? null : "Timeline is required",
      );
    case 9:
      return buildValidationResult(
        (data.housingSituation ? null : "Housing situation is required") ??
          (data.housingSituation === "renting"
            ? getLeaseExpirationError(data.leaseExpiration)
            : null),
      );
    case 10:
      return buildValidationResult(getEmailError(data.email));
    default:
      return buildValidationResult(null);
  }
}

export function formatDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateInput(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }

  const year = Number.parseInt(match[1]!, 10);
  const month = Number.parseInt(match[2]!, 10);
  const day = Number.parseInt(match[3]!, 10);
  const parsed = new Date(year, month - 1, day);

  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return startOfLocalDay(parsed);
}

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isOnboardingDataComplete(data: OnboardingData) {
  for (let step = 0; step < ONBOARDING_STEP_COUNT; step += 1) {
    if (!getOnboardingStepValidation(step, data).isValid) {
      return false;
    }
  }

  return true;
}

export function loadOnboardingSession() {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.sessionStorage.getItem(ONBOARDING_SESSION_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<OnboardingSession>;

    return {
      data: {
        ...getEmptyOnboardingData(),
        ...parsed.data,
      },
      isComplete: parsed.isComplete === true,
      marketplaceClaimToken: normalizeMarketplaceClaimToken(parsed.marketplaceClaimToken),
      marketplacePublishIntent: parsed.marketplacePublishIntent === true,
      referralAttributionToken: normalizeMarketplaceClaimToken(parsed.referralAttributionToken),
      step:
        typeof parsed.step === "number" &&
        parsed.step >= 0 &&
        parsed.step < ONBOARDING_STEP_COUNT
          ? parsed.step
          : 0,
    };
  } catch {
    return null;
  }
}

export function saveOnboardingSession(session: OnboardingSession) {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    ONBOARDING_SESSION_KEY,
    JSON.stringify(session),
  );
}

export function loadOnboardingResult() {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.sessionStorage.getItem(ONBOARDING_RESULT_KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as OnboardingResponse;
  } catch {
    return null;
  }
}

export function isCompletedOnboardingResult(
  result: CurrentUserResponse | null | undefined,
): result is OnboardingResponse {
  return Boolean(result?.user && result.profile && result.readiness);
}

/**
 * True when a /api/users/me result belongs to a non-consumer (pro) account —
 * an agent or loan officer. These accounts share the same Clerk session as the
 * consumer app but have no buyer profile, so the OnboardingGuard routes them to
 * the pro app instead of mounting buyer onboarding. Admin/consumer/absent all
 * return false (admins still onboard as buyers if they choose).
 */
export function isProRole(
  result: Pick<CurrentUserResponse, "role"> | null | undefined,
): boolean {
  return result?.role === "agent" || result?.role === "lo";
}

export function saveOnboardingResult(result: OnboardingResponse) {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(ONBOARDING_RESULT_KEY, JSON.stringify(result));
}

export function clearOnboardingResult() {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(ONBOARDING_RESULT_KEY);
}

export function clearOnboardingSession() {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(ONBOARDING_SESSION_KEY);
}

/**
 * Removes only the transient marketplace-claim handoff. Onboarding data stays
 * intact so an explicit claim discard never destroys a buyer's local resume
 * state. Call this only after a successful accept or an explicit discard.
 */
export function clearMarketplaceClaimToken(expectedToken?: string) {
  const session = loadOnboardingSession();
  if (!session) return;
  if (
    expectedToken !== undefined &&
    session.marketplaceClaimToken !== expectedToken
  ) {
    return;
  }
  saveOnboardingSession({ ...session, marketplaceClaimToken: null });
}

/**
 * Removes only a token whose attribution request completed. Keeping a token
 * on a transient request failure lets the authenticated bridge retry after a
 * normal navigation; it never retries after any successful service-owned
 * no-op (revoked, ownerless, self, or first-touch already occupied).
 */
export function clearReferralAttributionToken(expectedToken?: string) {
  const session = loadOnboardingSession();
  if (!session) return;
  if (
    expectedToken !== undefined &&
    session.referralAttributionToken !== expectedToken
  ) {
    return;
  }
  saveOnboardingSession({ ...session, referralAttributionToken: null });
}

function buildValidationResult(error: string | null): ValidationResult {
  return {
    error,
    isValid: error === null,
  };
}

function validateCurrencyValue(
  value: number | null,
  { label, max, min, required = true }: CurrencyValidationOptions,
) {
  if (value === null) {
    return required ? `${label} is required` : null;
  }

  if (!Number.isFinite(value)) {
    return `Enter a valid ${label.toLowerCase()}`;
  }

  if (value < min) {
    if (min === 0) {
      return `${label} must be $0 or more`;
    }

    if (min === 1) {
      return `${label} must be greater than $0`;
    }

    return `${label} must be at least $${min.toLocaleString()}`;
  }

  if (value > max) {
    return `${label} must be below $${max.toLocaleString()}`;
  }

  return null;
}
